/**
 * E2E speech-result injection (dev/E2E builds only).
 *
 * The iOS Simulator cannot feed real microphone audio to the Azure Speech
 * recognizer: the sim's only mic source is the Mac's physical microphone via
 * an acoustic loop (speaker → room → mic) that is gated by macOS permission,
 * degraded by echo cancellation, and unusable in automation — every recording
 * NoMatches. To make the voice-recording flows deterministically testable, an
 * E2E dev intent (`dev.queue-speech-result`) queues a canned outcome here and
 * `ConversationSpeech` consumes it in place of the native recognizer.
 *
 * Safety:
 * - Consume-once: cleared after a single recording, so a later real session
 *   (or the next test branch) is unaffected.
 * - Never active in production: the only producer is the `__DEV__`-stripped
 *   dev-intent handler, and `consumeE2ESpeechResult` additionally returns null
 *   unless `E2E_MODE` is on, so a stray queued value can never divert a
 *   production recording.
 */

import { E2E_MODE } from '../config/constants';

/** Scored happy-path result. Fields mirror PronunciationResult's score set. */
export interface E2ESpeechScores {
  accuracy: number;
  fluency: number;
  completeness: number;
  pronunciation: number;
}

export type E2ESpeechInjection =
  | {
      /** Recognized speech with pronunciation scores → a scored voice message. */
      kind: 'result';
      transcription: string;
      scores: E2ESpeechScores;
      /** How long to hold the "recording" state before completing (default 600ms). */
      delayMs?: number;
    }
  | {
      /** No usable speech → drives the recorder's "no speech detected" branch. */
      kind: 'no-speech';
      delayMs?: number;
    }
  | {
      /** A recognition failure (e.g. service error) → error overlay. */
      kind: 'error';
      code: string;
      message: string;
      delayMs?: number;
    };

let queued: E2ESpeechInjection | null = null;

/** Arm the next recording to resolve with this canned outcome (dev-intent only). */
export function queueE2ESpeechResult(injection: E2ESpeechInjection): void {
  queued = injection;
}

/**
 * Consume the queued outcome for the current recording, if any. Returns null
 * in production (E2E_MODE off) or when nothing is queued.
 */
export function consumeE2ESpeechResult(): E2ESpeechInjection | null {
  if (!E2E_MODE) return null;
  const value = queued;
  queued = null;
  return value;
}

/** Drop any armed outcome without consuming it (test teardown). */
export function clearE2ESpeechResult(): void {
  queued = null;
}
